/**
 * Issue #38 — capture + reporting des crashes JS côté browser.
 *
 * Pipeline :
 *   1. Setup `window.onerror` + `unhandledrejection` au démarrage (avant
 *      React mount, dans main.tsx).
 *   2. React `<RouteErrorBoundary>` rapporte via `reportClientError(err, ctx)`.
 *   3. Le reporter calcule fingerprint, applique dedup + throttle + sampling
 *      + circuit breaker, puis POST `/api/v1/client-errors`.
 *
 * Garanties :
 *   * 100% des 1ère occurrences sont POST (jamais skippées par sampling).
 *   * 5 POST/min max par session (anti-boucle).
 *   * Circuit breaker 60s après 3 échecs successifs (anti-spam backend).
 *   * Dedup par fingerprint en session (jamais 2x le même payload).
 *   * Sampling 1/100 sur les occurrences subséquentes du même fingerprint.
 *   * 404 (backend pas encore déployé) = silent fail, pas de retry.
 *
 * `lastAction` est synchronisé via QueryCache.subscribe() dans
 * `frontend/src/lib/queryActionTracker.ts` (wire-in dans App.tsx).
 */

import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

const ENDPOINT = "/api/v1/client-errors"

export type ErrorSourceType = "boundary" | "onerror" | "unhandledrejection" | "api_refusal"

export type ReportContext = {
  /** location.pathname + location.search au moment du crash. */
  route?: string
  /** Dernière mutation/query TanStack Query déclenchée avant le crash. */
  lastAction?: string | null
}

type Payload = {
  error_id_client: string
  fingerprint: string
  error_name: string
  error_message: string
  stack_trace: string | null
  source_type: ErrorSourceType
  route: string
  last_action: string | null
  user_agent: string
  app_version: string
}

type ReportResult = {
  errorId: string
  reported: boolean
}

// ----- État interne (en mémoire pendant la session navigateur) -----------

const seenFingerprints = new Set<string>()
const recentPostTimestamps: number[] = []
let circuitBreakerOpenUntil = 0
let consecutiveFailures = 0

const RATE_LIMIT_WINDOW_MS = 60_000
const RATE_LIMIT_MAX_POSTS = 5
const CIRCUIT_BREAKER_THRESHOLD = 3
const CIRCUIT_BREAKER_PAUSE_MS = 60_000
const SAMPLING_RATE_AFTER_FIRST = 0.01 // 1 / 100

/** Stocké par `queryActionTracker.ts` ; lu par le boundary au moment du crash. */
export let lastTrackedAction: string | null = null
export function setLastTrackedAction(action: string | null): void {
  lastTrackedAction = action
}

// ----- Fingerprinting ----------------------------------------------------

/**
 * Normalise un message d'erreur en remplaçant les valeurs variables
 * (UUIDs, nombres, chaînes entre guillemets) par des placeholders. Permet
 * de grouper deux erreurs sémantiquement identiques mais avec des données
 * différentes (ex: `Cannot find user "X"` et `Cannot find user "Y"`).
 */
function normalizeMessage(message: string): string {
  return message
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "<UUID>",
    )
    .replace(/\d+/g, "<N>")
    .replace(/(["'])[^"']*\1/g, "<STR>")
}

/**
 * SHA-1 hex de `name::normalized_message::top-3 frames`. Utilise WebCrypto
 * (`crypto.subtle.digest`) dispo sur tous les navigateurs modernes. Asynchrone.
 */
async function fingerprintError(name: string, message: string, stack: string | null): Promise<string> {
  const normalized = normalizeMessage(message)
  const topFrames = (stack ?? "").split("\n").slice(1, 4).join("\n")
  const raw = `${name}::${normalized}::${topFrames}`
  const buf = new TextEncoder().encode(raw)
  const digest = await crypto.subtle.digest("SHA-1", buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
}

// ----- Throttling / sampling --------------------------------------------

function shouldPost(fingerprint: string): boolean {
  // Circuit breaker ouvert : skip.
  if (Date.now() < circuitBreakerOpenUntil) return false

  // Rate-limit fenêtre glissante.
  const now = Date.now()
  while (recentPostTimestamps.length && recentPostTimestamps[0]! < now - RATE_LIMIT_WINDOW_MS) {
    recentPostTimestamps.shift()
  }
  if (recentPostTimestamps.length >= RATE_LIMIT_MAX_POSTS) return false

  // Sampling : 1ère occurrence = toujours, sinon 1/100.
  if (seenFingerprints.has(fingerprint)) {
    return Math.random() < SAMPLING_RATE_AFTER_FIRST
  }
  return true
}

// ----- POST helpers ------------------------------------------------------

/** Version envoyée hors build CI : l'API exige de 7 à 40 caractères. */
const LOCAL_DEV_VERSION = "local-dev"

async function postErrorPayload(payload: Payload): Promise<void> {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  })

  if (res.ok) {
    consecutiveFailures = 0
    recentPostTimestamps.push(Date.now())
    seenFingerprints.add(payload.fingerprint)
    return
  }

  // 404 = backend pas encore déployé (rolling deploy). Silent fail.
  if (res.status === 404) {
    console.warn("[errorReporter] backend not deployed yet (404), silent skip")
    return
  }

  // 401/403 = pas authentifié OU pas permis. Silent fail (l'utilisateur
  // verra quand même la page d'erreur, juste pas de log côté serveur).
  if (res.status === 401 || res.status === 403) {
    console.warn(`[errorReporter] not authorized (${res.status}), silent skip`)
    return
  }

  // 429 = rate-limited côté serveur. Dedupe ce fingerprint.
  if (res.status === 429) {
    seenFingerprints.add(payload.fingerprint)
    console.warn("[errorReporter] rate-limited (429), dedup fingerprint")
    return
  }

  // 5xx ou autre erreur transitoire : circuit breaker tick.
  consecutiveFailures += 1
  if (consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitBreakerOpenUntil = Date.now() + CIRCUIT_BREAKER_PAUSE_MS
    console.warn(
      `[errorReporter] circuit breaker OPEN for ${CIRCUIT_BREAKER_PAUSE_MS}ms after ${consecutiveFailures} failures`,
    )
  }
  throw new Error(`reporter POST failed: ${res.status}`)
}

// ----- Public API --------------------------------------------------------

export type ReportArgs = {
  error: Error | unknown
  sourceType: ErrorSourceType
  context?: ReportContext
}

/**
 * Reporte un crash JS. Retourne `{ errorId, reported }` :
 *   * `errorId` : UUID v4 unique, à afficher dans la page d'erreur.
 *   * `reported` : true si POST a réussi (banner peut dire "FGR notifié"),
 *     false si throttle/circuit/network/non-déployé (banner fallback).
 *
 * Ne throw jamais — toujours retourne un résultat, même si le POST échoue.
 */
export async function reportClientError(args: ReportArgs): Promise<ReportResult> {
  const errorIdClient = crypto.randomUUID()
  const errorObj = args.error instanceof Error ? args.error : new Error(String(args.error))
  const fingerprint = await fingerprintError(
    errorObj.name || "Error",
    errorObj.message || "",
    errorObj.stack ?? null,
  )

  if (!shouldPost(fingerprint)) {
    return { errorId: errorIdClient, reported: false }
  }

  const payload: Payload = {
    error_id_client: errorIdClient,
    fingerprint,
    error_name: errorObj.name || "Error",
    error_message: errorObj.message || "(no message)",
    stack_trace: errorObj.stack ?? null,
    source_type: args.sourceType,
    route: args.context?.route ?? `${window.location.pathname}${window.location.search}`,
    last_action: args.context?.lastAction ?? lastTrackedAction,
    user_agent: navigator.userAgent,
    app_version: import.meta.env.VITE_GIT_SHA ?? LOCAL_DEV_VERSION,
  }

  try {
    await postErrorPayload(payload)
    return { errorId: errorIdClient, reported: true }
  } catch (e) {
    // Réseau down ou 5xx : circuit breaker déjà ticked dans postErrorPayload.
    console.warn("[errorReporter] POST failed:", e)
    return { errorId: errorIdClient, reported: false }
  }
}

/**
 * Setup global `window.onerror` + `unhandledrejection`. À appeler UNE FOIS
 * dans `main.tsx`, AVANT `createRoot().render(<App/>)`. Capture les erreurs
 * async + event handlers natifs + pre-mount qui échappent à React
 * ErrorBoundary.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return

  window.addEventListener("error", (event) => {
    if (!event.error) return
    void reportClientError({
      error: event.error,
      sourceType: "onerror",
    })
  })

  window.addEventListener("unhandledrejection", (event) => {
    void reportClientError({
      error: event.reason ?? new Error("Unhandled promise rejection"),
      sourceType: "unhandledrejection",
    })
  })
}

// Refus d'API affichés à l'utilisateur.

const API_REFUSAL_PATH_MAX = 200
/** Un UUID complet, en minuscules ou majuscules. */
const UUID_SEGMENT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
/** Début de la requête ou du fragment d'une URL. */
const QUERY_OR_FRAGMENT = /[?#]/
/** Un segment de chemin entièrement numérique. */
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g
/** Un code métier sûr : 80 caractères, sans donnée libre. */
const SAFE_CODE = /^[A-Za-z0-9_:.-]{1,80}$/
/** Un segment d'au moins 20 caractères de jeton. */
const LONG_SEGMENT = /\/[A-Za-z0-9_-]{20,}(?=\/|$)/g
const DIGIT = /\d/
/** Partie de nom de route : lettres ou chiffres seuls. */
const ROUTE_KEYWORD_PART = /^(?:[A-Za-z]*|\d*)$/
const ROUTE_KEYWORD_SEPARATORS = /[-_]/
/** Session expirée, limite de plan (refus attendu) et débit. */
const API_REFUSAL_IGNORED_STATUSES = new Set([401, 402, 429])

/**
 * Masque un jeton, garde un nom de route.
 * Même règle que `mask_long_path_segments` côté serveur.
 */
function maskTokenSegment(segment: string): string {
  const isRouteKeyword = segment
    .slice(1)
    .split(ROUTE_KEYWORD_SEPARATORS)
    .every((part) => ROUTE_KEYWORD_PART.test(part))
  return DIGIT.test(segment) && !isRouteKeyword ? "/:token" : segment
}

/** Chemin d'API gabarit : `/a/<uuid>?q=1` devient `/a/:id`. */
function templateApiPath(url: string): string {
  return url
    .split(QUERY_OR_FRAGMENT, 1)
    .join("")
    .replace(UUID_SEGMENT, ":id")
    .replace(NUMERIC_SEGMENT, "/:id")
    .replace(LONG_SEGMENT, maskTokenSegment)
    .slice(0, API_REFUSAL_PATH_MAX)
}

/** Champ `key` d'un objet quelconque, absent pour toute autre valeur. */
function fieldOf(value: unknown, key: string): O.Option<unknown> {
  return pipe(
    O.fromNullable(value),
    O.filter((candidate): candidate is Record<string, unknown> => typeof candidate === "object"),
    O.chain((fields) => O.fromNullable(fields[key])),
  )
}

function isText(value: unknown): value is string {
  return typeof value === "string"
}

/**
 * Description d'un refus d'API : méthode, chemin gabarit, statut, code.
 * Rien pour un réseau coupé, un 401 ou un 429.
 * Jamais le détail libre ni le corps de la requête.
 *
 * @example
 * describeApiRefusal(error)
 */
export function describeApiRefusal(error: unknown): O.Option<{ message: string; route: string }> {
  const response = fieldOf(error, "response")
  const config = fieldOf(error, "config")
  return pipe(
    response,
    O.chain((served) => fieldOf(served, "status")),
    O.filter((status): status is number => typeof status === "number" && status >= 400 && !API_REFUSAL_IGNORED_STATUSES.has(status)),
    O.map((status) => {
      const safeCode = pipe(
        response,
        O.chain((served) => fieldOf(served, "data")),
        O.chain((answer) => fieldOf(answer, "code")),
        O.filter((code): code is string => isText(code) && SAFE_CODE.test(code)),
        O.getOrElse(() => "none"),
      )
      const method = pipe(
        config,
        O.chain((settings) => fieldOf(settings, "method")),
        O.filter(isText),
        O.map((verb) => verb.toUpperCase()),
        O.getOrElse(() => "?"),
      )
      const route = pipe(
        config,
        O.chain((settings) => fieldOf(settings, "url")),
        O.filter(isText),
        O.map(templateApiPath),
        O.getOrElse(() => ""),
      )
      return { message: `${method} ${route} -> ${status} ${safeCode}`, route }
    }),
  )
}

/**
 * Remonte un refus d'API vu par l'utilisateur dans un toast.
 * Mêmes garde-fous de débit que les crashs.
 * Cinq envois par minute, puis une empreinte sur cent.
 * Disjoncteur après trois échecs.
 *
 * @example
 * reportApiRefusal(error).then(() => undefined)
 */
export function reportApiRefusal(error: unknown): Promise<void> {
  return pipe(
    describeApiRefusal(error),
    O.match(
      () => Promise.resolve(),
      (refusal) => {
        const refused = new Error(refusal.message, { cause: error })
        refused.name = "ApiRefusal"
        delete refused.stack
        return reportClientError({
          error: refused,
          sourceType: "api_refusal",
          context: { route: refusal.route },
        }).then(() => undefined)
      },
    ),
  )
}

// ----- Test helpers (uniquement utilisés en tests) -----------------------

/** Reset l'état interne (dedup, rate-limit, circuit breaker). Usage tests. */
export function __resetReporterStateForTests(): void {
  seenFingerprints.clear()
  recentPostTimestamps.length = 0
  circuitBreakerOpenUntil = 0
  consecutiveFailures = 0
  lastTrackedAction = null
}
